import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import LinkedInExportGuide from './LinkedInExportGuide';
import { Spinner, SDMark } from '@/components/atoms';
import { profile as profileApi, auth as authApi } from '@/lib/api';
import { useProfileState } from '@/contexts/ProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { DEMO_PROFILE_TEXT } from '@/lib/constants';
import { trackEvent, EVENTS } from '@/lib/analytics';

const INSTRUCTIONS = [
  'Open linkedin.com and go to your profile',
  "Click the 'More ⋯' button (right below your profile photo)",
  "Select 'Save to PDF'",
  'Upload the downloaded file below',
];

export default function Step0Connect() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { patchDraft } = useProfileState();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [linkedinFile, setLinkedinFile] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [dragSlot, setDragSlot] = useState(null); // 'linkedin' | 'cv' | null
  const [signingOut, setSigningOut] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(
    () => !!user?.linkedin_connected && !user?.has_password,
  );

  // Post-Connect consent — LinkedIn follow only (newsletter is now handled
  // automatically at signup via the notice under the Sign-up button on
  // Welcome.jsx). We still fire a defensive newsletter opt-in on Continue
  // for users who arrived through non-standard paths (magic link, dev-login).
  const [followOnLinkedIn, setFollowOnLinkedIn] = useState(true);
  const [linkedInClicked, setLinkedInClicked] = useState(false); // visual "Following" state after popup opened
  const SD_LINKEDIN_URL = 'https://www.linkedin.com/company/sustainabilitydecoded/';

  // Open the SD LinkedIn page in a compact centred popup rather than a full new
  // tab \u2014 keeps the user visually anchored to the onboarding flow. We can't
  // programmatically confirm the follow (LinkedIn has no API for that), so we
  // trust-flip the card's UI to "Following" the moment the popup opens. If
  // popups are blocked, we fall back to a same-tab open.
  // Deprecated: popup-based Follow opener was replaced with a plain new-tab
  // link. Keeping this block-comment as history so a future reader knows why.

  // Show the LinkedIn Follow card ONLY to users with a LinkedIn footprint
  // (signed in with LinkedIn OAuth or uploaded a LinkedIn PDF). Users who
  // only uploaded a CV never see it - asking a non-LinkedIn user to follow
  // us on LinkedIn is friction, not service.
  const hasLinkedInFootprint = !!user?.linkedin_connected || !!linkedinFile;

  // Clicking Follow now stays entirely inside the app - no popup, no new tab.
  // We simply flip the button to "Following" and record the intent to the
  // backend on Continue. Users still have the "preview the page" link in the
  // footer if they want to visit LinkedIn directly.
  const openLinkedInFollow = () => {
    setLinkedInClicked(true);
    setFollowOnLinkedIn(true);
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch (err) {
      // Best-effort logout; still navigate away. Log the reason so we can
      // spot recurring session-drop bugs instead of silently swallowing.
      console.warn('[Step0Connect] logout failed but proceeding:', err?.message || err);
    }
    navigate('/', { replace: true });
  };

  const goNext = (text, extracted, source) => {
    patchDraft({
      ...(extracted || {}),
      cv_text: text || '',
      cv_source: source,
      input_type: source === 'linkedin_pdf' ? 'linkedin_pdf' : source,
    });
    setTimeout(() => navigate('/onboarding/confirm'), 700);
  };

  const validateFile = (file) => {
    if (!file) return 'No file selected.';
    if (!/\.(pdf|docx|txt)$/i.test(file.name)) return 'Please upload a .pdf, .docx, or .txt file.';
    if (file.size > 8 * 1024 * 1024) return 'File too large (max 8 MB).';
    return null;
  };

  const stageFile = (slot, file) => {
    setError(null); setSuccess(null);
    const err = validateFile(file);
    if (err) { setError(err); return; }
    // Instrumentation — a PDF (or DOCX/TXT) was accepted. Fire-and-forget.
    try {
      const kind = (file?.name || '').toLowerCase().split('.').pop() || 'file';
      trackEvent(EVENTS.pdf_uploaded, { slot, kind: kind.slice(0, 10) });
    } catch (_e) { /* no-op */ }
    if (slot === 'linkedin') setLinkedinFile(file);
    else setCvFile(file);
  };

  const clearSlot = (slot) => {
    setError(null); setSuccess(null);
    if (slot === 'linkedin') setLinkedinFile(null);
    else setCvFile(null);
  };

  const handleSubmit = async () => {
    if (busy || (!linkedinFile && !cvFile)) return;
    setError(null); setSuccess(null); setBusy(true);
    // Fire-and-forget the consent record BEFORE extraction so we capture the
    // intent even if extraction fails. Non-blocking — a failure here should
    // never gate the analysis flow.
    profileApi.newsletterConsent({
      newsletter_opt_in: true,  // auto-opt-in per signup notice; backend is idempotent
      linkedin_follow_intent: hasLinkedInFootprint && followOnLinkedIn,
      source: 'onboarding',
    }).catch(() => { /* silent — consent is best-effort */ });
    // If the user chose to follow us on LinkedIn AND hasn't already opened
    // the popup via the mini-card, open it now inside this click handler so
    // Follow-on-LinkedIn no longer opens a tab automatically. The Follow
    // button on the LinkedIn card is a self-contained toggle - clicking it
    // flips the local "Following" state and records the intent to backend.
    // No popup, no window.open, no browser hijack.
    try {
      const data = await profileApi.extractCombined({ linkedinFile, cvFile });
      const ex = data.extracted || {};
      const name = ex.name || 'your profile';
      const title = ex.title || 'role to confirm';
      const roles = data.roles_detected || 0;
      const bothMsg = linkedinFile && cvFile ? ' (merged from LinkedIn + CV)' : '';
      setSuccess(`Found: ${name} — ${title}${roles ? ` — ${roles} role${roles === 1 ? '' : 's'} detected` : ''}${bothMsg}`);
      goNext(data.text, ex, data.source || 'linkedin_pdf');
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to parse your file(s).');
      setBusy(false);
    }
  };

  const handleDemo = async () => {
    setError(null); setSuccess(null); setBusy(true);
    try {
      const data = await profileApi.extractText(DEMO_PROFILE_TEXT, 'demo');
      setSuccess(`Loaded demo: ${data.extracted?.name || 'Sarah Chen'} — ${data.extracted?.title || 'Senior ESG Analyst'}`);
      goNext(DEMO_PROFILE_TEXT, data.extracted, 'demo');
    } catch (e) {
      setError('Demo failed to load.');
      setBusy(false);
    }
  };

  const onDropSlot = (slot) => (e) => {
    e.preventDefault(); setDragSlot(null);
    const f = e.dataTransfer.files?.[0];
    if (f) stageFile(slot, f);
  };

  const hasAnyFile = !!(linkedinFile || cvFile);
  const hasBoth = !!(linkedinFile && cvFile);

  return (
    <OnboardingLayout step={0}>
      {/* Full-screen busy overlay — prevents the app appearing "frozen" while
          the backend parses the uploaded documents. Sits above OnboardingLayout
          content but below any modal (modals use higher z-index). */}
      {busy && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-6 backdrop-blur-sm"
          style={{ background: 'rgba(10, 12, 10, 0.62)' }}
          role="status"
          aria-live="polite"
          data-testid="connect-submit-overlay"
        >
          <div
            className="max-w-[420px] w-full bg-white rounded-card shadow-2xl p-7 text-center"
            style={{ border: '1px solid rgba(76,217,100,0.18)' }}
          >
            <div className="mx-auto mb-4 flex items-center justify-center" style={{ width: 56, height: 56 }}>
              <Spinner size={44} />
            </div>
            <div className="text-[18px] font-semibold text-sd-black">
              Analyzing your {hasBoth ? 'documents' : (linkedinFile ? 'LinkedIn profile' : 'CV')}…
            </div>
            <div className="mt-2 text-[13px] text-sd-mid-gray leading-relaxed">
              {hasBoth
                ? 'Processing your LinkedIn profile / CV and preparing your first pass.'
                : 'Extracting your roles, skills and experience. This usually takes 5–15 seconds.'}
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#1F4D2A]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4CD964] animate-pulse" />
              Please don't refresh the page
            </div>
          </div>
        </div>
      )}

      {showPwdModal && <PasswordSetupModal onDone={() => setShowPwdModal(false)} />}

      <p className="text-[12px] uppercase tracking-[0.14em] text-sd-mid-gray font-medium">Step 1 of 4</p>
      <h2 className="text-[34px] font-semibold text-sd-black mt-2 mb-3 heading-tight">Now let’s get your full profile</h2>

      {/* Sign-in confirmation card — LinkedIn profile preview at top */}
      {user && (
        <div
          className="rounded-card p-5 mb-6 flex items-center gap-4 border border-[rgba(76,217,100,0.18)] relative"
          style={{ background: 'linear-gradient(135deg, #F5F7F5 0%, #FFFFFF 100%)' }}
          data-testid="signed-in-card"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-16 h-16 rounded-full object-cover shadow-sm"
              /* LinkedIn's media CDN (media.licdn.com) rejects image requests
                 that carry a Referer header from a different origin. Setting
                 referrerpolicy="no-referrer" strips the header so the image
                 loads. Also `crossorigin` is left unset so the browser makes
                 a plain image request (no CORS preflight). */
              referrerPolicy="no-referrer"
              onError={(e) => {
                /* Fallback if the LinkedIn URL 4xx-errors (expired token,
                   scoped ACL, etc.) — swap for the same initial-avatar we
                   render when picture is missing. */
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent && !parent.querySelector('.sd-avatar-fallback')) {
                  const div = document.createElement('div');
                  div.className = 'sd-avatar-fallback w-16 h-16 rounded-full bg-gradient-to-br from-sd-primary to-[#3FB854] text-white text-[22px] font-semibold flex items-center justify-center shadow-sm';
                  div.textContent = (user.name || user.email || 'SD')[0]?.toUpperCase() || 'S';
                  parent.insertBefore(div, e.currentTarget);
                }
              }}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sd-primary to-[#3FB854] text-white text-[22px] font-semibold flex items-center justify-center shadow-sm">
              {(user.name || user.email || 'SD')[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[17px] font-semibold text-sd-black truncate">{user.name || user.email}</div>
            <div className="text-[13px] text-sd-mid-gray truncate">{user.email}</div>
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#1F4D2A] bg-[#E9FAEC] rounded-pill px-2.5 py-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {user.linkedin_connected ? 'Signed in with LinkedIn' : 'Signed in'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut || busy}
            className="absolute top-3 right-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="signed-in-signout-btn"
            aria-label="Sign out"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}

      <p className="text-sd-mid-gray text-[14.5px] mb-5 max-w-[640px] leading-relaxed">
        To build your career intelligence, we need your full work history. Upload your <strong className="text-sd-black">LinkedIn PDF export</strong> and/or your <strong className="text-sd-black">CV</strong>. <span className="text-sd-black font-medium">The more detail we have, the sharper your analysis will be</span> — if you upload both, we'll analyze them together.
      </p>

      {/* Instructions — 2-column: left has heading + numbered steps; right
          has the animated visual guide which now spans the full column
          height (starts flush with the heading, not below it). */}
      <div className="rounded-card p-5 mb-6 border border-sd-light-gray bg-white">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-5 md:gap-6 items-start">
          {/* Left column — heading + numbered steps */}
          <div>
            <div className="text-[13.5px] font-semibold text-sd-black mb-3">How to export your LinkedIn PDF</div>
            <ol className="space-y-2">
              {INSTRUCTIONS.map((step, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="w-[22px] h-[22px] rounded-full bg-sd-primary text-white text-[11.5px] font-semibold flex items-center justify-center shrink-0 mt-0.5 tabular-nums">{i + 1}</span>
                  <div className="text-[13.5px] text-sd-dark-gray leading-relaxed pt-0.5">{step}</div>
                </li>
              ))}
            </ol>
            <div className="text-[12px] text-sd-mid-gray mt-3 flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Takes about 10 seconds.
            </div>
          </div>

          {/* Right column — animated LinkedIn export walkthrough
              Sits flush with the top of the heading in the left column. */}
          <div className="w-full max-w-[360px] justify-self-center md:justify-self-end">
            <LinkedInExportGuide />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-card border-l-4 border-sd-error bg-[#FDF2F2] text-[#76313B] p-4 mb-5 text-[14px] animate-fade-in" data-testid="connect-error">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-card border-l-4 border-sd-primary bg-[#E9FAEC] text-[#1F4D2A] p-4 mb-5 text-[14px] animate-fade-in" data-testid="connect-success">✓ {success}</div>
      )}

      {/* Dual upload zones — LinkedIn PDF + CV (both optional, at least one required) */}
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="text-[13.5px] font-semibold text-sd-black">Upload your profile</div>
        <div className="text-[11.5px] text-sd-mid-gray">Upload one — or both</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-4">
        <DropSlot
          slot="linkedin"
          label="LinkedIn PDF"
          subLabel="Exported from your profile"
          accent="#0A66C2"
          accentBgSoft="#EEF5FC"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
              <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5V9h3v10zM6.5 7.7a1.7 1.7 0 110-3.4 1.7 1.7 0 010 3.4zM19 19h-3v-5.2c0-1.2 0-2.8-1.7-2.8-1.7 0-2 1.3-2 2.7V19h-3V9h2.9v1.4h.04A3.2 3.2 0 0115 8.9c3 0 4 2 4 4.6V19z"/>
            </svg>
          }
          file={linkedinFile}
          onFile={(f) => stageFile('linkedin', f)}
          onClear={() => clearSlot('linkedin')}
          isDragging={dragSlot === 'linkedin'}
          onDragOver={(e) => { e.preventDefault(); setDragSlot('linkedin'); }}
          onDragLeave={() => setDragSlot(null)}
          onDrop={onDropSlot('linkedin')}
          disabled={busy}
        />

        <DropSlot
          slot="cv"
          label="CV / Résumé"
          subLabel=".pdf, .docx or .txt"
          accent="#4CD964"
          accentBgSoft="#E9FAEC"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="14" y2="17" />
            </svg>
          }
          file={cvFile}
          onFile={(f) => stageFile('cv', f)}
          onClear={() => clearSlot('cv')}
          isDragging={dragSlot === 'cv'}
          onDragOver={(e) => { e.preventDefault(); setDragSlot('cv'); }}
          onDragLeave={() => setDragSlot(null)}
          onDrop={onDropSlot('cv')}
          disabled={busy}
        />
      </div>

      {/* Consent card — appears once at least one file is staged. Only the
          LinkedIn-follow ask lives here now; newsletter opt-in is handled
          automatically at signup (see the notice under the Sign up button on
          Welcome.jsx) so we don't ask twice. The card renders only for users
          with a LinkedIn footprint OR to show the mission note if none. */}
      {hasAnyFile && !busy && (hasLinkedInFootprint || true) && (
        <div
          className="mb-4 p-4 rounded-[14px] border border-sd-hairline bg-sd-ivory"
          data-testid="connect-consent-card"
        >
          <div className="sd-consent-mission" data-testid="consent-mission-note">
            <div className="sd-consent-mission__body">
              <p>
                <span className="sd-consent-mission__hi">SD-Pivot is completely free</span>. Please help us with this step that enables us to keep powering the mission — building tools that decode the sustainability job market for people already working in it.
              </p>
            </div>
          </div>

          {/* LinkedIn Follow card — rendered ONLY for users with a LinkedIn
              footprint (OAuth login or LinkedIn PDF upload). CV-only users
              never see this so we don't ask non-LinkedIn users to follow us
              on LinkedIn. LinkedIn deprecated their official Follow embed in
              2019 and iframes are blocked by X-Frame-Options, so this card
              is a visual analog that opens LinkedIn in a plain new tab. */}
          {hasLinkedInFootprint && (
          <div className="mt-3 sd-linkedin-embed" data-testid="linkedin-follow-embed">
            <div className="sd-linkedin-embed__banner" />
            <div className="sd-linkedin-embed__body">
              <div className="sd-linkedin-embed__logo" aria-hidden="true">
                <SDMark size={40} radius={0.22} bg="#000000" markTop="#FFFFFF" markBottom="#44C349" />
              </div>
              <div className="sd-linkedin-embed__info">
                <div className="sd-linkedin-embed__name">Sustainability Decoded</div>
                <div className="sd-linkedin-embed__tag">Newsletter · We replace opinion-based sustainability with systems-based understanding.</div>
              </div>
              <button
                type="button"
                onClick={openLinkedInFollow}
                className={`sd-linkedin-embed__btn ${linkedInClicked ? 'is-following' : ''}`}
                data-testid="linkedin-follow-btn"
                aria-pressed={linkedInClicked}
              >
                {linkedInClicked ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Following
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Follow
                  </>
                )}
              </button>
            </div>
            {linkedInClicked && (
              <div className="sd-linkedin-embed__toast" data-testid="linkedin-follow-toast">
                Following · <strong>thanks for the support.</strong> We'll deliver on it.
              </div>
            )}
          </div>
          )}

          <div className="mt-2 text-[11.5px] text-sd-mid-gray">
            Unsubscribe anytime · we never share your email{hasLinkedInFootprint ? (
              <>{' '}· <a href={SD_LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-sd-black">preview the page</a></>
            ) : null}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={busy || !hasAnyFile}
        className="sd-btn-primary w-full !py-3.5 text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        data-testid="connect-submit-btn"
      >
        {busy ? (
          <>
            <Spinner size={18} />
            <span>Reading your profile{hasBoth ? 's' : ''}…</span>
          </>
        ) : hasBoth ? (
          <>Analyze both documents →</>
        ) : hasAnyFile ? (
          <>Continue with {linkedinFile ? 'LinkedIn' : 'CV'} →</>
        ) : (
          <>Upload at least one file to continue</>
        )}
      </button>

      {/* Consent card was moved ABOVE the button so users see the two checkboxes
          before they click Continue. The block below is intentionally empty. */}

      {hasBoth && !busy && (
        <div className="mt-3 text-[12.5px] text-sd-mid-gray flex items-center gap-1.5" data-testid="connect-both-hint">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          We'll merge both, dedupe roles, and prefer whichever source has richer detail per field.
        </div>
      )}

      <section className="mt-7 flex items-center justify-between text-[13px] text-sd-mid-gray">
        <button onClick={handleDemo} className="underline hover:text-sd-black" disabled={busy} data-testid="connect-demo">Try demo profile</button>
        <span>Step 1 of 4</span>
      </section>
    </OnboardingLayout>
  );
}

// ---------------------------------------------------------------------------
// DropSlot — one of two upload targets (LinkedIn PDF or CV)
// Handles: drag/drop, click-to-browse, staged-file preview with remove button.
// ---------------------------------------------------------------------------
function DropSlot({
  slot,
  label,
  subLabel,
  accent,
  accentBgSoft,
  icon,
  file,
  onFile,
  onClear,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  disabled,
}) {
  const inputId = `sd-drop-${slot}`;
  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Staged (file present) view
  if (file) {
    return (
      <div
        className="rounded-card border-2 border-dashed p-4 flex items-start gap-3 transition-colors"
        style={{ borderColor: accent, background: accentBgSoft }}
        data-testid={`drop-slot-${slot}-staged`}
      >
        <div className="w-10 h-10 rounded-lg bg-white shadow-card flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: accent }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {label} ready
          </div>
          <div className="text-[13.5px] text-sd-black font-medium mt-0.5 truncate" title={file.name}>{file.name}</div>
          <div className="text-[11.5px] text-sd-mid-gray mt-0.5">{fmtSize(file.size)}</div>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="text-[12px] font-medium text-sd-mid-gray hover:text-sd-error disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 mt-0.5"
          data-testid={`drop-slot-${slot}-remove`}
          aria-label={`Remove ${label}`}
        >
          Remove
        </button>
      </div>
    );
  }

  // Empty view (drop / browse)
  return (
    <label
      htmlFor={inputId}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`block rounded-card text-center py-6 px-4 cursor-pointer transition-all border-2 border-dashed ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      style={{
        borderColor: isDragging ? accent : '#E3E5E3',
        background: isDragging ? accentBgSoft : '#FFFFFF',
      }}
      data-testid={`drop-slot-${slot}`}
    >
      <input
        id={inputId}
        type="file"
        accept=".pdf,.docx,.txt"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        disabled={disabled}
        data-testid={`drop-slot-${slot}-input`}
      />
      <div className="w-10 h-10 mx-auto mb-2 rounded-lg flex items-center justify-center" style={{ background: accentBgSoft }}>
        {icon}
      </div>
      <div className="text-[14.5px] font-semibold text-sd-black">{label}</div>
      <div className="text-[12px] text-sd-mid-gray mt-0.5">{subLabel}</div>
      <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: accent }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Drop or browse
      </div>
      <div className="text-[10.5px] text-sd-mid-gray mt-1.5">.pdf, .docx, .txt · max 8 MB</div>
    </label>
  );
}

function PasswordSetupModal({ onDone }) {
  const { refresh } = useAuth();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 8) { setErr('Use at least 8 characters.'); return; }
    if (pwd !== confirm) { setErr('Passwords don’t match.'); return; }
    setBusy(true);
    try {
      await authApi.setPassword(pwd);
      await refresh();
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not set password.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 animate-fade-in">
      <div className="bg-white rounded-card shadow-card-deep p-7 max-w-[440px] w-full">
        <h3 className="text-[22px] font-semibold text-sd-black mb-2">Set a password</h3>
        <p className="text-sd-mid-gray text-[13.5px] mb-5 leading-relaxed">
          So you can sign in with email + password next time too — not just LinkedIn. This becomes your Sustainability Decoded account.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" placeholder="Choose a password (min 8 chars)" className="sd-input" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="setpwd-pwd" />
          <input type="password" placeholder="Confirm password" className="sd-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="setpwd-confirm" />
          {err && <div className="text-[13px] text-sd-error">{err}</div>}
          <div className="flex justify-between items-center pt-2">
            <button type="button" className="text-[13px] text-sd-mid-gray hover:text-sd-black" onClick={onDone} data-testid="setpwd-skip">Skip for now</button>
            <button type="submit" className="sd-btn-primary" disabled={busy} data-testid="setpwd-submit">{busy ? 'Saving…' : 'Save password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
